/**
 * Lazy-loads collection products in header mega-menu (desktop) and drawer (mobile)
 * via Shopify Section Rendering API.
 *
 * Desktop:
 * - Init: prefetch first tab (panel 0)
 * - Hover tabs: debounced load
 *
 * Mobile:
 * - Load when a drawer accordion / sub-accordion opens
 */
(() => {
  if (window.__headerNavLazyInit) return;
  window.__headerNavLazyInit = true;

  const LAZY_SELECTOR = '[data-header-nav-lazy-products]';
  const SECTION_ID = 'section-rendering-header-nav-tab-products';
  const HOVER_DEBOUNCE_MS = 150;
  const contentCache = new Map();
  const emptyCache = new Map();
  const inflightKeys = new Set();

  function debounce(fn, wait) {
    let timer = null;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        fn(...args);
      }, wait);
    };
  }

  function getCacheKey(element) {
    const handle = element.dataset.collectionHandle?.trim();
    const limit = element.dataset.limit || '4';
    const cardStyle = element.dataset.cardStyle || 'grid';
    return handle ? `products:${handle}:${limit}:${cardStyle}` : null;
  }

  function buildFetchUrl(element) {
    const handle = element.dataset.collectionHandle?.trim();
    if (!handle) return null;

    const limit = element.dataset.limit || '4';
    const cardStyle = element.dataset.cardStyle || 'grid';
    const url = new URL(`/collections/${handle}`, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    url.searchParams.set('limit', limit);
    url.searchParams.set('card_style', cardStyle);
    return url.toString();
  }

  function clearPlaceholderState(element) {
    element.querySelectorAll('[data-header-nav-lazy-placeholder]').forEach((node) => node.remove());
  }

  function setLazyState(element, state) {
    element.classList.remove(
      'header-nav-lazy--pending',
      'header-nav-lazy--loading',
      'header-nav-lazy--loaded',
      'header-nav-lazy--error',
      'header-nav-lazy--empty',
    );
    element.classList.add(`header-nav-lazy--${state}`);

    const error = element.querySelector('[data-header-nav-lazy-error]');
    const empty = element.querySelector('[data-header-nav-lazy-empty]');

    if (error instanceof HTMLElement) error.hidden = state !== 'error';
    if (empty instanceof HTMLElement) empty.hidden = state !== 'empty';
  }

  function applyLazyContent(element, html, isEmpty) {
    if (isEmpty) {
      clearPlaceholderState(element);
      setLazyState(element, 'empty');
      element.dataset.loaded = 'true';
      return;
    }

    clearPlaceholderState(element);
    element.insertAdjacentHTML('beforeend', html);
    setLazyState(element, 'loaded');
    element.dataset.loaded = 'true';
  }

  async function loadLazyPanel(element) {
    if (element.dataset.loaded === 'true') return;

    const cacheKey = getCacheKey(element);
    if (!cacheKey) {
      setLazyState(element, 'error');
      element.dataset.loaded = 'true';
      return;
    }

    if (contentCache.has(cacheKey)) {
      applyLazyContent(element, contentCache.get(cacheKey) ?? '', emptyCache.get(cacheKey) === true);
      return;
    }

    if (inflightKeys.has(cacheKey)) return;

    const fetchUrl = buildFetchUrl(element);
    if (!fetchUrl) {
      setLazyState(element, 'error');
      element.dataset.loaded = 'true';
      return;
    }

    inflightKeys.add(cacheKey);
    setLazyState(element, 'loading');

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Header nav lazy load failed');

      const html = await response.text();
      const innerHtml =
        new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-header-nav-lazy-content]')
          ?.innerHTML.trim() ?? '';
      const isEmpty = innerHtml === '';

      contentCache.set(cacheKey, innerHtml);
      emptyCache.set(cacheKey, isEmpty);

      document.querySelectorAll(LAZY_SELECTOR).forEach((panel) => {
        if (
          panel instanceof HTMLElement &&
          getCacheKey(panel) === cacheKey &&
          panel.dataset.loaded !== 'true'
        ) {
          applyLazyContent(panel, innerHtml, isEmpty);
        }
      });
    } catch {
      clearPlaceholderState(element);
      setLazyState(element, 'error');
      element.dataset.loaded = 'true';
    } finally {
      inflightKeys.delete(cacheKey);
    }
  }

  function isPanelInOpenAccordions(panel) {
    let node = panel.parentElement;
    while (node) {
      if (node.matches?.('details') && !node.open) return false;
      if (node.id === 'menu-drawer' || node.classList?.contains('mega_menu__inner')) break;
      node = node.parentElement;
    }
    return true;
  }

  function loadLazyIn(container) {
    if (!(container instanceof HTMLElement)) return;

    container.querySelectorAll(`${LAZY_SELECTOR}:not([data-loaded="true"])`).forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      // Mobile: skip panels still nested inside a closed <details>
      if (!isPanelInOpenAccordions(panel)) return;
      loadLazyPanel(panel);
    });
  }

  function loadFirstTabPanels() {
    document.querySelectorAll('.mega_menu__inner [data-tab-panel="0"]').forEach((panel) => {
      if (panel instanceof HTMLElement) loadLazyIn(panel);
    });
  }

  function setupTabTriggers() {
    const loadTabPanel = debounce((trigger) => {
      const inner = trigger.closest('.mega_menu__inner');
      const index = trigger.dataset.tabIndex;
      const panel = inner?.querySelector(`[data-tab-panel="${index}"]`);
      if (panel instanceof HTMLElement) loadLazyIn(panel);
    }, HOVER_DEBOUNCE_MS);

    document.querySelectorAll('.mega_menuLeft [data-tab-trigger]').forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        loadTabPanel(trigger);
      });
    });
  }

  function setupMobileAccordionTriggers() {
    const drawer = document.querySelector('header-drawer #menu-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        loadLazyIn(details);
      });
    });
  }

  function init() {
    setupTabTriggers();
    setupMobileAccordionTriggers();
    // Prefetch first desktop tab products on page load
    loadFirstTabPanels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
